<script>
  import CoinSprite from '../components/CoinSprite.svelte'
  import Icon from '../components/Icon.svelte'
  import satoshiCoin from '../assets/icon/satoshi-circle.svg'
  import satoshiCoinDataURI from '../assets/satoshi-data-uri.js'
  import config from '../config.js'
  import { scale } from 'svelte/transition'
  import { liveFxRates } from '../stores.js'
  import { tweened } from 'svelte/motion'
  import { cubicInOut } from 'svelte/easing'
  import { calcRate, calcNumFiatCoins } from '../utils/currency.js'
  import { getFxRates } from '../api/api.js'
  // import { fractionalInterpolate } from '../utils/interpolate.js'
  import { createEventDispatcher } from 'svelte'

  const dispatch = createEventDispatcher()

  export let currency
  let coin
  let unit
  $: {
    if (currency && currency.coins && currency.coins.length) {
      coin = currency.coins[0]
      unit = currency.units[coin.unit]
    }
  }

  export let flipped

  let numCoins = tweened(0, { duration: 1500, easing: cubicInOut })
  let numSats = tweened(0, { duration: 1500, easing: cubicInOut })
  let fixFiat = true
  let fiatSprites = []
  let satSprites = []
  let fiatRate

  init()
  async function init () {
    await getFxRates()
    updateCoinSets()
  }


  function updateCoinSets (fxRates, currency, coin, unit, flipped) {
    if (fxRates && fxRates.USD && currency && coin && unit) {
      fiatRate = calcRate(currency, fxRates)
      const fiatValue = unit.value * coin.units
      if (flipped) {
        numCoins.set(1)
        let amount = (fiatValue / fiatRate) * 100000000
        amount = Math.round(amount * 1000) / 1000
        numSats.set(amount)
      } else {
        numSats.set(1)
        let amount = calcNumFiatCoins(currency, fxRates)
        let dpFactor = Math.pow(10,currency.maxDP)
        amount = Math.round(amount * dpFactor) / dpFactor
        numCoins.set(amount)
      }
    }
  }

  $: {
    updateCoinSets($liveFxRates, currency, coin, unit, flipped)
  }

  $: {
    fiatSprites = generateSprites($numCoins)
  }
  $: {
    satSprites = generateSprites($numSats)
  }

  function pluralize (amount, singular, plural) {
    if (amount === 1) return singular
    else return plural
  }

  function generateSprites (count) {
    const hasFractional = count > Math.floor(count)
    const sprites = []
    if (count > 0) {
      const columns = Math.ceil(Math.sqrt(Math.ceil(count)))
      const rows = Math.ceil(count / columns)
      const width = 100 / columns
      const offset = (100 - (rows * width)) / 2
      for (let i = 0; i <= (hasFractional ? count : count - 1); i++) {
        sprites.push({
          x: Math.floor(i % columns) * width,
          y: offset + (Math.floor(i / columns) * width),
          w: width,
          fraction: (i > count - 1) ? count % 1 : 1
        })
      }
    }
    return sprites
  }

  function formatDecimal (num, maxDP=2) {
    return Number(num.toFixed(maxDP))
  }

  function flip () {
    dispatch('flip')
  }
</script>

<style type="text/scss">
  .comparison {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    transition: transform 1s;
    will-change: transform;

    .side {
      width: 0px;
      flex-grow: 1;
      flex-shrink: 1;
      position: relative;
      color: var(--palette-bold-a);
      text-align: center;
      transition: transform 1s;
      will-change: transform;

      .currency {
        margin: 0;
        font-size: 0.8rem;
      }

      .name {
        margin-top: 0;
        font-size: 2rem;
        margin-bottom: 0.5rem;
        text-transform: capitalize;
        font-variant: all-small-caps;
      }

      .coins {
        .coinset {
          max-width: 80%;
          width: 256px;
          max-height: 45vh;
        }
      }
    }

    .equals {
      position: relative;
      color: var(--palette-bold-a);
      font-size: 50px;

      &::before {
        content: '⤹';
        position: absolute;
        font-size: .5em;
        bottom: 0;
        right: 75%;
        transform: rotate(-45deg) scaleY(-1);
        transition: transform 1s;
        will-change: transform;
      }
      &::after {
        content: '⤸';
        position: absolute;
        font-size: .5em;
        top: 0;
        left: 75%;
        transform: rotate(-45deg) scaleY(1);
        transition: transform 1s;
        will-change: transform;
      }
    }

    &.flipped {
      transform: rotate(180deg);
      transform-origin: center;

      .side {
        transform: rotate(-180deg);
        transform-origin: center;
      }

      .equals {
        &::before {
          transform: rotate(-45deg) scaleY(1);
        }
        &::after {
          transform: rotate(-45deg) scaleY(-1);
        }
      }
    }
  }

  @media screen and (max-width: 900px) and (orientation: portrait) {
    .comparison {
      flex-direction: column;
      align-items: stretch;

      .side {
        width: 100%;
        text-align: center;

        .coins {
          .coinset {
            max-width: 90%;
            width: 256px;
            max-height: calc(45vh - 10rem);
          }
        }

        .name {
          font-size: 1.6rem;
        }
      }

      .equals {
        width: 1em;
        margin: auto;
      }
    }
  }
</style>

<div class="comparison" class:flipped>
  {#if currency && coin }
    <div class="side sats">
      <p class="currency">Bitcoin (BTC)</p>
      <p class="name">{ formatDecimal($numSats) } { pluralize($numSats, 'satoshi', 'satoshis') }</p>
      <div class="coins">
        <svg class="coinset"
           viewBox="0 0 100 100"
           version="1.1">
           {#each satSprites as sprite, index }
             <CoinSprite img="{satoshiCoinDataURI}" sprite={sprite} index={index} />
           {/each}
        </svg>
        <!-- <img src="/build/img/coins/satoshi-coin.svg" alt="Satoshi"> -->
      </div>
    </div>
    <button class="equals" on:click={flip}>&thickapprox;</button>
    <div class="side fiat">
      <p class="currency">{ currency.name } ({currency.altCode || currency.code})</p>
      <p class="name">{ formatDecimal($numCoins * coin.units) } { pluralize($numCoins * coin.units, unit.name, unit.plural) }</p>
      <div class="coins">
        <svg class="coinset"
           viewBox="0 0 100 100"
           version="1.1">
          <!-- <defs>
            <filter id="fiatImg" x="0%" y="0%" height="100%" width="100%">
              <feImage xlink:href="{coin.img}" />
            </filter>
          </defs> -->
          {#each fiatSprites as sprite, index }
            <CoinSprite img={coin.img} sprite={sprite} index={index} />
          {/each}
        </svg>
      </div>
    </div>
  {/if}
</div>
